<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Qualification - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <incident-download-buttons
            :incident="incident"
            class="btn-group btn-group-sm"
          ></incident-download-buttons>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
        </template>
      </page-header>
      <claim-handler></claim-handler>

      <div class="card mb-3">
        <incident-tabs
          :tabs="tabs"
          :source="source"
          :incident="incident"
          :module="module"
          v-on:selected="setActive"
          :active="currentView"
        ></incident-tabs>

        <transition-group name="component-fade" mode="out-in">
          <validation
            :incident="incident"
            :readonly="readonly"
            v-show="currentView == 'validation'"
            v-on:showDiary="showDiary"
            key="validation"
          ></validation>

          <incident-diary
            :url="'/qualification/' + incident"
            :incident="incident"
            :readonly="readonly"
            :slim="true"
            v-show="currentView == 'diary'"
            key="diary"
          ></incident-diary>

          <incident-source
            :incident="incident"
            :showCancel="showCancel"
            :readonly="readonly"
            v-on:created="activateTabs"
            v-on:exists="activateTabs"
            v-if="currentView == 'source'"
            v-on:unsaved-data="saveStatus"
            key="source"
          ></incident-source>

          <accident-details
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'accident_description'"
            v-on:unsaved-data="saveStatus"
            key="accidentDetails"
          ></accident-details>

          <client
            :incident="incident"
            :module="module"
            :readonly="readonly"
            v-if="currentView == 'client'"
            v-on:unsaved-data="saveStatus"
            key="client"
          ></client>

          <third-parties
            :incident="incident"
            :module="module"
            :readonly="readonly"
            v-if="currentView == 'third_parties'"
            v-on:unsaved-data="saveStatus"
            key="third_parties"
          ></third-parties>

          <witnesses
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'witnesses'"
            key="witnesses"
          ></witnesses>

          <estimate
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'estimate'"
            v-on:unsaved-data="saveStatus"
            key="estimate"
          ></estimate>

          <statements
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'statements'"
            v-on:unsaved-data="saveStatus"
            key="statements"
          ></statements>

          <documents
            :incident="incident"
            :readonly="readonly"
            v-if="currentView == 'documents'"
            key="documents"
          ></documents>

          <email
            :incident="incident"
            v-if="currentView == 'email'"
            key="email"
          ></email>
        </transition-group>

        <!--<div class="card-footer text-right" v-if="currentView == 'details'">-->
        <!--<button class="btn btn-primary btn-raised" name="save" v-on:click.prevent="submit">-->
        <!--<i class="icon icon-save"></i> Save-->
        <!--</button>-->
        <!--</div>-->
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import IncidentDiary from './../Shared/IncidentDiary.vue';
import Notes from './../Shared/Notes.vue';
import Validation from './Children/Validation.vue';
import Source from './../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from './../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from './../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from './../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from './../FirstNoticeOfLoss/Sections/Witnesses.vue';
import Estimate from './../FirstNoticeOfLoss/Sections/Estimate.vue';
import Statements from './../FirstNoticeOfLoss/Sections/Statements.vue';
import Documents from './../Shared/Documents.vue';
import Email from './../Shared/Email.vue';
import IncidentTabs from '../Shared/IncidentTabs';
import IncidentDownloadButtons from '../Shared/IncidentDownloadButtons';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    PageHeader,
    PageHeaderAction,
    IncidentDiary,
    'notes-window': Notes,
    validation: Validation,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    Estimate,
    Documents,
    Email,
    Statements,
    IncidentTabs,
    IncidentDownloadButtons
  },

  data() {
    return {
      module: 'Qualification',
      notes: false,
      currentView: this.$route.hash
        ? this.$route.hash.replace('#', '')
        : 'validation',
      unsaved_data: false,
      showCancel: false,
      downloading: false,
      tabs: [
        {
          name: 'validation',
          label: 'Validation',
          disabled: false
        },
        {
          name: 'diary',
          label: 'Diary',
          disabled: false
        }
      ]
    };
  },

  computed: {
    source() {
      return this.$store.getters['Incident/source'];
    },

    readonly() {
      return this.isReadonly();
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];
      if (source && source.claim_reference) {
        return source.claim_reference;
      }
      return null;
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.dispatch('Incident/retrieve', this.incident);
  },

  methods: {
    showDiary() {
      if (this.currentView !== 'diary') {
        this.currentView = 'diary';
      }
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    activateTabs() {
      this.tabs.forEach(tab => {
        tab.disabled = false;
      });
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
